import {useAxios} from "./useAxios";
import {useCallback, useEffect, useRef, useState} from "react";

const ITEMS_PER_PAGE = 20;

export default function useKennisitems(
  ingelogdeGebruikerId,
  cat,
  prod,
  typen,
  vid,
  taggroep,
  tekst,
  unlock,
  dash,
  inhoud,
  vers,
  kenmerk,
  fav,
  initData = null
) {
    const {get} = useAxios();

    const smartEnabled = !!tekst;

    // Seed from SSR initData so the page renders immediately without a blank state.
    const initItems = initData?.data || [];
    const hasSSRData = initItems.length > 0;
    const [pages, setPages] = useState(hasSSRData ? [{data: initItems}] : []);
    const [isLoading, setIsLoading] = useState(!hasSSRData);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [size, setSize] = useState(1);
    const [total, setTotal] = useState(initData?.total ?? null);
    const [lastPage, setLastPage] = useState(initData?.last_page ?? null);
    // Pre-seed the ref with the current fetchKey when SSR already gave us data —
    // this prevents the client from immediately re-fetching what SSR already loaded.
    const fetchKey = JSON.stringify([
        tekst, cat, prod, typen, vid, taggroep, unlock, dash, inhoud, vers, kenmerk, fav,
        ingelogdeGebruikerId,
    ]);
    const fetchedForRef = useRef(hasSSRData ? fetchKey : null);

    // Semantic search endpoint — requires tekst.
    const buildSmartUrl = (pageNum) => {
        const params = new URLSearchParams();
        params.set('zoek', tekst);
        params.set('page', String(pageNum));
        params.set('aantalPerPagina', String(ITEMS_PER_PAGE));
        if (cat)    params.set('categorie', String(parseInt(cat)));
        if (taggroep) params.set('taggroep', String(parseInt(taggroep)));
        if (fav)    params.set('showBookmarked', 'true');
        if (vid)    params.set('filterVideo', 'true');
        if (unlock) params.set('autorisatie', unlock);
        if (prod)   params.set('product', prod);
        if (inhoud === 'release') {
            params.set('typen', '3');
            if (vers)   params.set('versies', vers);
            if (kenmerk) params.set('kenmerk', kenmerk);
        } else {
            params.set('notTypen', '3');
            if (typen) params.set('typen', typen);
        }
        return `/mijn-mkg/support/kenniscentrum/zoeken?${params.toString()}`;
    };

    // Broad (filter-only) endpoint — works without tekst.
    const buildBroadUrl = (pageNum) => {
        const params = new URLSearchParams();
        params.set('page', String(pageNum));
        params.set('aantalPerPagina', String(ITEMS_PER_PAGE));
        if (tekst)   params.set('tekst', tekst);
        if (cat)    params.set('categorie', String(parseInt(cat)));
        if (taggroep) params.set('taggroep', String(parseInt(taggroep)));
        if (fav)    params.set('showBookmarked', 'true');
        if (vid)    params.set('filterVideo', 'true');
        if (unlock) params.set('autorisatie', unlock);
        if (prod)   params.set('product', prod);
        if (inhoud === 'release') {
            params.set('typen', '3');
            if (vers)   params.set('versies', vers);
            if (kenmerk) params.set('kenmerk', kenmerk);
        } else {
            params.set('notTypen', '3');
            if (typen) params.set('typen', typen);
        }
        return `/wiki/zoeken?${params.toString()}`;
    };

    const buildUrl = (pageNum) => smartEnabled ? buildSmartUrl(pageNum) : buildBroadUrl(pageNum);

    // Main effect: fetch page 1 whenever the search query or filters change.
    useEffect(() => {
        if (fetchedForRef.current === fetchKey) return;
        fetchedForRef.current = fetchKey;

        let cancelled = false;
        setIsLoading(true);

        (async () => {
            try {
                const resp = await get(buildUrl(1));
                if (cancelled) return;
                const data       = resp?.data?.data || [];
                const respTotal  = resp?.data?.total ?? data.length;
                const respLast   = resp?.data?.last_page ?? 1;
                setTotal(respTotal);
                setLastPage(respLast);
                setSize(1);
                setPages([{data}]);
            } catch {
                if (!cancelled) {
                    setPages([]);
                    setTotal(0);
                    setLastPage(1);
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [fetchKey, smartEnabled]);

    // Pagination effect: fetch the next page when size is incremented by the scroll trigger.
    useEffect(() => {
        if (pages.length >= size) return;

        let cancelled = false;
        setIsLoadingMore(true);

        (async () => {
            try {
                const newPages = [];
                for (let i = pages.length; i < size; i++) {
                    const resp = await get(buildUrl(i + 1));
                    if (cancelled) return;
                    const data = resp?.data?.data || [];
                    newPages.push({data});
                    const t  = resp?.data?.total;
                    const lp = resp?.data?.last_page;
                    if (t  !== undefined) setTotal(t);
                    if (lp !== undefined) setLastPage(lp);
                }
                if (!cancelled && newPages.length > 0) {
                    setPages(prev => [...prev, ...newPages]);
                }
            } finally {
                if (!cancelled) setIsLoadingMore(false);
            }
        })();

        return () => { cancelled = true; };
    }, [size, smartEnabled]);

    // Reached the end when we've loaded all pages the backend reported.
    const isReachingEnd = lastPage !== null && size >= lastPage;

    const mutate = useCallback(() => {
        fetchedForRef.current = null;
        setPages([]);
        setTotal(null);
        setLastPage(null);
        setSize(1);
    }, []);

    return {
        data: pages,
        isLoading,
        isLoadingMore,
        isReachingEnd,
        isRefreshing: false,
        mutate,
        size,
        setSize,
        total,
    };
}
